import React, {
    ComponentProps,
    ComponentPropsWithoutRef,
    useContext,
    useEffect,
    useRef,
} from 'react';
import { Row as IRow } from '@tanstack/table-core';
import { faChevronDown, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TableContext } from 'components/table/TableContext';
import { componentOrFragmentFactory } from 'components/util';

interface TableRowExpandProps<T> extends ComponentPropsWithoutRef<'button'> {
    row: IRow<T>;
}

const TableRowExpand = <T,>(props: TableRowExpandProps<T>) => {
    const { row, type = 'reset', onClick, ...rest } = props;
    const { table } = useContext(TableContext);
    const isExpanded = row.getIsExpanded();
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (ref.current) {
            const td = ref.current.closest('td');
            td?.classList.add('!px-0', '!w-10', '[&>*]:!px-0');
            ref.current.parentElement?.classList.add(
                '!px-0',
                'flex',
                'items-center',
                'justify-center',
            );
        }
    }, []);

    const PossibleButton = componentOrFragmentFactory(
        (props: ComponentProps<'button'>) => React.createElement('button', props),
        !table.options.meta?.to,
    );

    return (
        <div ref={ref}>
            <PossibleButton
                type={type}
                onClick={e => {
                    row.toggleExpanded(!isExpanded);
                    onClick?.(e);
                }}
                {...rest}
            >
                <FontAwesomeIcon
                    className={'text-gray-700'}
                    icon={isExpanded ? faChevronDown : faChevronRight}
                />
            </PossibleButton>
        </div>
    );
};

export { TableRowExpand };
export type { TableRowExpandProps };
